import React from 'react';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  FormGroup,
  Col
} from 'reactstrap';
import { FontAwesomeIcon } from '../../../components/Icons';
import ItemList, { ProductVariantItem } from './ItemList';
import { apiClient } from '../../../utils/Api';

const defaultParams = {
  per_page: 15,
  page: 1,
  sort_by: 'updatedAt_desc'
};

const defaultPaginations = {
  from: 0,
  to: 0,
  total: 0,
  currentPage: 0,
  lastPage: 0,
  perPage: 0
};

class ModalProductVariants extends React.Component {
  constructor(props){
    super(props);
    this._isMountedFlag = false;
    this._isSaving = false;
    this.state = {
      _params: {
        ...defaultParams
      },
      paginations: {
        ...defaultPaginations
      },
      list: [],
      listSelected: [],
      isLoading: false,
      _alertErrorOpen: false,
      _errorMessage: ''
    };
  }

  componentDidMount(){
    this._isMountedFlag = true;
  }

  componentWillUnmount(){
    this._isMountedFlag = false;
  }

  _safeSetState = payload => {
    if(this._isMountedFlag){
      this.setState(payload);
    }
  }

  componentDidUpdate(prevProps){
    if(this.props.isOpen && this.props.isOpen !== prevProps.isOpen){
      this._safeSetState({
        _params: {
          ...this.state._params,
          ...defaultParams,
          category_id: this.props.dataCategories.length > 0 && this.state._params.category_id === '' ?
            this.props.dataCategories[0].value :
            (this.state._params.category_id ? this.state._params.category_id : '')
        },
        paginations: {
          ...defaultPaginations
        },
        list: [],
        listSelected: [],
        _alertErrorOpen: false,
        _errorMessage: ''
      });
    }
  }

  _sanitizeParams = rawParams => {
    return {
      params: Object.keys(rawParams)
      .filter(key => rawParams[key] !== '')
      .reduce((obj, current) => ({ ...obj, [current]: rawParams[current] }), {})
    };
  }

  _getDataList = (rawParams = {}) => {
    this._safeSetState({ isLoading: true });
    apiClient().get('/admins/productvariants', this._sanitizeParams(rawParams))
    .then(({ data }) => {
      const { data: rows, ...paginations } = data.data || {};
      const selectedIds = this.props.selectedItems.map(item => item.id);
      const filteredRows = rows.filter(item => !selectedIds.includes(item.id));
      this._safeSetState({
        list: filteredRows.map(item => item),
        paginations: {
          from: paginations.from,
          to: paginations.to,
          total: paginations.total,
          currentPage: paginations.current_page,
          lastPage: paginations.last_page,
          perPage: paginations.per_page
        },
        _alertErrorOpen: false,
        _errorMessage: ''
      });
    })
    .catch(() => {
      this._safeSetState({
        list: [],
        paginations: {
          ...defaultPaginations
        },
        _alertErrorOpen: true,
        _errorMessage: 'Failed to load product variants. Please try again.'
      });
    })
    .finally(() => {
      this._safeSetState({ isLoading: false });
    });
  }

  _handleGoto = page => {
    const _params = {
      ...this.state._params,
      page
    };
    this.setState({ _params }, () => this._getDataList(this.state._params));
  }

  _handleOnSubmit = async () => {
    if(this.props.isSubmitting || this._isSaving){
      return;
    }
    const { onSubmit, toggle } = this.props;
    const { listSelected } = this.state;
    if(listSelected.length === 0){
      return;
    }
    this._isSaving = true;
    try{
      const onSubmitResult = onSubmit(listSelected);
      const isSucceeded = onSubmitResult && typeof onSubmitResult.then === 'function'
        ? await onSubmitResult
        : onSubmitResult;
      if(isSucceeded !== false){
        toggle();
      }
    }
    finally{
      this._isSaving = false;
    }
  }

  _handleSelected = (itemId, checked) => {
    this.setState(prevState => ({
      listSelected: checked ?
        [...prevState.listSelected, itemId] :
        prevState.listSelected.filter(id => id !== itemId)
    }));
  }

  render(){
    return(
      <Modal isOpen={this.props.isOpen} toggle={this.props.toggle} size={this.props.size}>
        <ModalBody>
          <Alert
            color="danger"
            isOpen={this.state._alertErrorOpen}
            toggle={() => this.setState({_alertErrorOpen: false})}
          >
            {this.state._errorMessage}
          </Alert>
          <Form
            style={{marginBottom: '.5rem'}}
            onSubmit={e => {
              e.preventDefault();
              this._getDataList(this.state._params);
            }}
          >
            <FormGroup row style={{marginBottom: '.5rem'}}>
              <Col sm={4}>
                <Input
                  bsSize="sm"
                  type="select"
                  name="category_id"
                  value={this.state._params.category_id || ''}
                  onChange={e => {
                    const { name, value } = e.target;
                    this.setState(prevState => ({
                      _params: {
                        ...prevState._params,
                        [name]: value
                      }
                    }));
                  }}
                >
                  <option value="">All Category</option>
                  {this.props.dataCategories.map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </Input>
              </Col>
            </FormGroup>
            <InputGroup>
              <Input
                bsSize="sm"
                placeholder="Search products..."
                value={this.state._params.keyword || ''}
                name="keyword"
                onChange={e => {
                  const { name, value } = e.target;
                  this.setState(prevState => ({
                    _params: {
                      ...prevState._params,
                      [name]: value
                    }
                  }));
                }}
              />
              <InputGroupAddon addonType="append">
                <Button color="primary">Search</Button>
              </InputGroupAddon>
            </InputGroup>
          </Form>
          <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center'}}>
            <div>
              {this.state.listSelected.length > 0 ? (
                <p style={{margin: 0, fontSize: '.5rem'}} className="text-muted">Selected ({this.state.listSelected.length}) item(s).</p>
              ) : null}
            </div>
            <div>
              {this.state.listSelected.length > 0 ? (
                <Button
                  outline
                  color="danger"
                  size="sm"
                  type="button"
                  className="mr-1"
                  style={{fontSize: '.5rem'}}
                  onClick={() => this.setState({listSelected: []})}
                >
                  Clear Selected
                </Button>
              ) : null}
              <Button
                outline
                title="Refresh list"
                color="primary"
                style={{fontSize: '.5rem'}}
                size="sm"
                type="button"
                onClick={() => this._handleGoto(1)}
              >
                <FontAwesomeIcon iconType="refresh"/>
              </Button>
            </div>
          </div>
          <ItemList
            isLoading={this.state.isLoading}
            paginations={this.state.paginations}
            list={this.state.list}
            renderItems={item => (
              <ProductVariantItem
                key={item.id}
                data={item}
                checked={this.state.listSelected.includes(item.id)}
                onSelectedChange={({target: { checked }}) => {
                  this._handleSelected(item.id, checked);
                }}
              />
            )}
            onGotoPage={this._handleGoto}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.props.toggle} disabled={this.props.isSubmitting}>Cancel</Button>
          <Button color="primary" onClick={this._handleOnSubmit} disabled={this.props.isSubmitting}>Save</Button>
        </ModalFooter>
      </Modal>
    );
  }
}

ModalProductVariants.defaultProps = {
  size: 'md',
  selectedItems: [],
  isSubmitting: false
};

export default ModalProductVariants;
